<template>
  <div>
    <RightSidebar :isActive.sync="isActive" size="large">
      <template #header>
        <img width="40" :src="OrderIcon" class="mr-4" />
        <div>
          <div class="flex items-center">
            <div><h4>Z report</h4></div>
          </div>
          <small v-if="zReport">{{ zReport.endOfDayReportId.slice(0, 8) }}</small>
        </div>
      </template>

      <template #content>
        <Loading v-if="isLoading" />
        <div v-else>
          <section class="px-6 pt-6 grid grid-cols-4 gap-x-8">
            <SidebarItem label="Rekom opening day">
              <div class="flex flex-col">
                <div class="flex">
                  <feather-icon class="mr-1" icon="CalendarIcon" svgClasses="text-grey w-5 h-5" />
                  {{ zReport.date | date }}
                </div>
              </div>
            </SidebarItem>

            <SidebarItem label="Time of entry">
              <div class="flex flex-col">
                <div class="flex">
                  <feather-icon class="mr-1" icon="CalendarIcon" svgClasses="text-grey w-5 h-5" />
                  {{ zReport.createdAction.timestamp | formatDate }}
                </div>
                <div class="text-grey ml-6">By {{ zReport.createdAction.userDisplayName }}</div>
              </div>
            </SidebarItem>

            <SidebarItem label="Terminal">
              <div class="flex flex-col">
                <div class="flex">
                  <code>
                    {{ zReport.metadata.name }}
                  </code>
                </div>
              </div>
            </SidebarItem>

            <SidebarItem label="Venue">
              <div class="flex flex-col">
                <div class="flex">
                  <code>
                    {{ zReport.receiptVenue.name }}
                  </code>
                </div>
              </div>
            </SidebarItem>
          </section>

          <vs-divider />

          <section class="px-6 pt-2 grid grid-cols-2 gap-x-12">
            <div>
              <h4 class="mb-2">Total sales</h4>
              <div class="grid grid-cols-2 gap-x-4 ml-2">
                <div>Subtotal excl. VAT:</div>
                <div class="justify-self-end" style="font-family: 'Courier New'">
                  {{ zReport.totalSalesAndVat.subtotalExVat | formatCurrency }} DKK
                </div>

                <div v-for="(taxRate, i) in zReport.totalSalesAndVat.vatByPercentage" :key="i" class="contents">
                  <div>{{ taxRate.group }}:</div>
                  <div class="justify-self-end" style="font-family: 'Courier New'">{{ taxRate.vatAmount | formatCurrency }} DKK</div>
                </div>

                <div class="font-bold">Total sales:</div>
                <div class="font-bold justify-self-end" style="font-family: 'Courier New'">
                  {{ zReport.totalSalesAndVat.totalSales | formatCurrency }} DKK
                </div>
              </div>
            </div>

            <div>
              <h4 class="mb-2">Balance</h4>
              <div class="grid grid-cols-2 gap-x-4 ml-2">
                <div>Total sales:</div>
                <div class="justify-self-end" style="font-family: 'Courier New'">{{ zReport.balance.totalSales | formatCurrency }} DKK</div>

                <div>Total cash sales:</div>
                <div class="justify-self-end" style="font-family: 'Courier New'">
                  {{ zReport.balance.totalCashSales | formatCurrency }} DKK
                </div>

                <div>Cash at open:</div>
                <div class="justify-self-end" style="font-family: 'Courier New'">{{ zReport.balance.cashAtOpen | formatCurrency }} DKK</div>

                <div>Cash at close:</div>
                <div class="justify-self-end" style="font-family: 'Courier New'">
                  {{ zReport.balance.cashAtClose | formatCurrency }} DKK
                </div>

                <div>Cash difference:</div>
                <div
                  class="justify-self-end"
                  style="font-family: 'Courier New'"
                  :class="{ 'text-red font-bold': zReport.balance.cashDifference != 0 }"
                >
                  {{ zReport.balance.cashDifference | formatCurrency }} DKK
                </div>

                <div>Tips:</div>
                <div class="justify-self-end" style="font-family: 'Courier New'">{{ zReport.balance.tips | formatCurrency }} DKK</div>

                <div class="font-bold">Total revenue</div>
                <div class="font-bold justify-self-end" style="font-family: 'Courier New'">
                  {{ zReport.balance.totalRevenue | formatCurrency }} DKK
                </div>
              </div>
            </div>
          </section>

          <section class="px-6 pt-6 mt-4">
            <h4 class="mb-2">Sales by sales groups</h4>
            <div class="grid grid-cols-2 gap-4">
              <vs-table :data="zReport.salesGroupSales">
                <template #thead>
                  <vs-th sort-key="groupName"> Sales group </vs-th>
                  <vs-th sort-key="amount" class="flex justify-end"> Amount </vs-th>
                </template>
                <template slot-scope="{ data }">
                  <vs-tr :data="tr" :key="tr.id" v-for="tr in data">
                    <vs-td class="py-0">
                      {{ tr.groupName }}
                    </vs-td>
                    <vs-td class="py-0 text-right" style="font-family: 'Courier New'"> {{ tr.amount | formatCurrency }} DKK </vs-td>
                  </vs-tr>
                </template>
              </vs-table>
              <VueApexCharts
                type="bar"
                :options="chartOptions(zReport.salesGroupSales, 'groupName')"
                :series="series(zReport.salesGroupSales, 'Total DKK by sales group', 'amount')"
              ></VueApexCharts>
            </div>
          </section>

          <section class="px-6 pt-6">
            <h4 class="mb-2">Payment methods</h4>
            <div class="grid grid-cols-2 gap-4">
              <vs-table :data="zReport.paymentsByMethod">
                <template #thead>
                  <vs-th sort-key="method">Type</vs-th>
                  <vs-th sort-key="numberOfPayments"> Qty </vs-th>
                  <vs-th sort-key="totalAmount" class="flex justify-end"> Amount </vs-th>
                </template>
                <template slot-scope="{ data }">
                  <vs-tr :data="tr" :key="tr.id" v-for="tr in data">
                    <vs-td class="py-0">
                      {{ tr.method }}
                    </vs-td>
                    <vs-td class="py-0">
                      {{ tr.numberOfPayments }}
                    </vs-td>
                    <vs-td class="py-0 text-right" style="font-family: 'Courier New'"> {{ tr.totalAmount | formatCurrency }} DKK </vs-td>
                  </vs-tr>
                </template>
              </vs-table>
              <VueApexCharts
                type="bar"
                :options="chartOptions(zReport.paymentsByMethod, 'method')"
                :series="series(zReport.paymentsByMethod, 'Total DKK by payment type', 'totalAmount')"
              ></VueApexCharts>
            </div>
          </section>

          <section class="px-6 pt-6">
            <h4 class="mb-2">Sales by card type</h4>
            <div class="grid grid-cols-2 gap-4">
              <vs-table :data="zReport.paymentsByType">
                <template #thead>
                  <vs-th sort-key="paymentType">Type</vs-th>
                  <vs-th sort-key="totalAmount" class="flex justify-end"> Amount </vs-th>
                </template>
                <template slot-scope="{ data }">
                  <vs-tr :data="tr" :key="tr.id" v-for="tr in data">
                    <vs-td class="py-0">
                      {{ tr.paymentType }}
                    </vs-td>
                    <vs-td class="py-0 text-right" style="font-family: 'Courier New'"> {{ tr.totalAmount | formatCurrency }} DKK </vs-td>
                  </vs-tr>
                </template>
              </vs-table>
              <VueApexCharts
                type="bar"
                :options="chartOptions(zReport.paymentsByType, 'paymentType')"
                :series="series(zReport.paymentsByType, 'Total sales by card type', 'totalAmount')"
              ></VueApexCharts>
            </div>
          </section>

          <section class="px-6 pt-6">
            <h4 class="mb-2">Sales by clerk</h4>
            <div class="grid grid-cols-2 gap-4">
              <vs-table :data="zReport.salesByClerk">
                <template #thead>
                  <vs-th sort-key="clerkName">Clerk name</vs-th>
                  <vs-th sort-key="amount" class="flex justify-end"> Amount </vs-th>
                </template>
                <template slot-scope="{ data }">
                  <vs-tr :data="tr" :key="tr.id" v-for="tr in data">
                    <vs-td class="py-0">
                      {{ tr.clerkName }}
                    </vs-td>
                    <vs-td class="py-0 text-right" style="font-family: 'Courier New'"> {{ tr.amount | formatCurrency }} DKK </vs-td>
                  </vs-tr>
                </template>
              </vs-table>
              <VueApexCharts
                type="bar"
                :options="chartOptions(zReport.salesByClerk, 'clerkName')"
                :series="series(zReport.salesByClerk, 'Total sales by clerk', 'amount')"
              ></VueApexCharts>
            </div>
          </section>
        </div>
      </template>
    </RightSidebar>
  </div>
</template>

<script>
import OrderIcon from "@/assets/images/order-icon.png";
import RightSidebar from "@/components/Custom/RightSidebar.vue";
import NumberInput from "@/components/Custom/NumberInput.vue";
import DateInput from "@/components/Custom/DateInput.vue";
import SidebarItem from "@/components/Custom/SidebarItem.vue";
import VueApexCharts from "vue-apexcharts";

export default {
  components: {
    RightSidebar,
    NumberInput,
    DateInput,
    SidebarItem,
    VueApexCharts
  },
  data() {
    return {
      OrderIcon,

      isActive: false,
      isLoading: true,

      zReport: null,

      venue: null
    };
  },
  computed: {},
  methods: {
    async show(zReportId) {
      this.isActive = true;
      this.isLoading = true;

      var result = await this.$store.dispatch("pos/zReports/getZReportsById", { id: zReportId });

      this.zReport = result;
      console.log("Got zReport", this.zReport);

      this.isLoading = false;
    },
    chartOptions(iterable, nameString) {
      const labels = iterable.map(item => {
        // Cut name length to a maximum of 15
        let name = item[nameString];
        // if (name.length > 15) {
        //   name = name.substring(0, 15) + "...";
        // }
        return name;
      });

      return {
        xaxis: {
          categories: labels
        }
      };
    },
    series(iterable, seriesName, amountString) {
      return [
        {
          name: seriesName,
          data: iterable.map(item => {
            return item[amountString];
          })
        }
      ];
    }
  }
};
</script>

<style scoped>
.text-red {
  color: red;
}
</style>
